import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Key, Loader2, CheckCircle, Copy } from 'lucide-react';
import { auth } from '@/lib/firebase';
import { workspaceKeysApi } from '@/lib/api/workspace-keys';
import { KeyWithSecret } from '@optidev/shared-types';
import toast from 'react-hot-toast';

interface OptiSignsApiKeyModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export function OptiSignsApiKeyModal({ isOpen, onClose, onSuccess }: OptiSignsApiKeyModalProps) {
  const [generating, setGenerating] = useState(false);
  const [generatedKey, setGeneratedKey] = useState<KeyWithSecret | null>(null);

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const token = await auth.getIdToken();
      if (!token) {
        toast.error('Authentication required');
        return;
      }

      const result = await workspaceKeysApi.generate(token);
      setGeneratedKey(result);
      toast.success('API key generated successfully');
    } catch (error: any) {
      console.error('Failed to generate key:', error);
      toast.error(error.response?.data?.message || 'Failed to generate key');
    } finally {
      setGenerating(false);
    }
  };

  const handleCopySecret = async () => {
    if (!generatedKey) return;
    await navigator.clipboard.writeText(generatedKey.secret);
    toast.success('Secret copied to clipboard');
  };

  const handleContinue = () => {
    setGeneratedKey(null);
    onClose();
    onSuccess();
  };

  const handleClose = () => {
    setGeneratedKey(null);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[450px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Key className="h-5 w-5 text-primary" />
            API Key Required
          </DialogTitle>
          <DialogDescription>
            An API key is needed to securely connect your project to OptiSigns.
          </DialogDescription>
        </DialogHeader>

        {!generatedKey ? (
          <div className="space-y-4 py-4">
            <p className="text-sm text-muted-foreground">
              This key will be used to authenticate your OptiDev projects with OptiSigns.
              You only need to generate this once per workspace.
            </p>

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={handleClose} disabled={generating}>
                Cancel
              </Button>
              <Button onClick={handleGenerate} disabled={generating}>
                {generating ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Generating...
                  </>
                ) : (
                  'Generate Key'
                )}
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-4 py-4">
            <Card className="border-green-500/20 bg-green-50/50 dark:bg-green-900/10">
              <CardContent className="pt-6">
                <div className="flex gap-3">
                  <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-green-700 dark:text-green-300">
                      API key generated successfully!
                    </p>
                    <p className="text-xs text-green-600 dark:text-green-400 mt-1">
                      You can manage this key in Settings → API Keys.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <div className="space-y-2">
              <Label>Key ID</Label>
              <code className="block text-sm font-mono bg-muted px-3 py-2 rounded border">
                {generatedKey.keyId}
              </code>
            </div>

            <div className="space-y-2">
              <Label>Secret</Label>
              <div className="flex gap-2">
                <code className="flex-1 text-sm font-mono bg-muted px-3 py-2 rounded border break-all">
                  {generatedKey.secret}
                </code>
                <Button variant="outline" size="icon" onClick={handleCopySecret} title="Copy secret">
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button onClick={handleContinue}>
                Continue to OptiSigns
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
